import campaignsJson from './fixtures/campaigns.json'
import creativesJson from './fixtures/w2/creatives.json'
import type {
  CreativeCompliance,
  CreativeKind,
  CreativeSource,
  LibraryCreative,
  Product,
} from './types'

interface RawCreative {
  id: string
  name: string
  kind: CreativeKind
  duration: string | null
  source: 'ai' | 'up' | 'kol'
  kol: string | null
  product: string
  usedByAds: number
  compliance: CreativeCompliance | null
  cover: string
  clip: string | null
}

interface CreativeFixture {
  items: RawCreative[]
}

interface CampaignFixture {
  products: Product[]
}

const fixture = creativesJson as unknown as CreativeFixture
const products = (campaignsJson as unknown as CampaignFixture).products
const productIdByName = new Map(products.map((product) => [product.name, product.id]))
const licenseDates: Record<string, string> = {
  'cr-03': '2027-03-31',
  'cr-07': '2027-04-30',
  'cr-10': '2027-02-28',
  'cr-15': '2026-10-01',
  'cr-23': '2027-06-30',
  'cr-24': '2027-05-31',
}

function sourceOf(source: RawCreative['source']): CreativeSource {
  return source === 'up' ? 'upload' : source
}

function seededDate(id: string): string {
  const sequence = Number(id.slice(3))
  return `2026-08-${String(32 - Math.min(sequence, 24)).padStart(2, '0')}`
}

export const initialCreatives: LibraryCreative[] = fixture.items.map((creative) => {
  const productId = productIdByName.get(creative.product)
  if (!productId) throw new Error(`素材 ${creative.id} 引用了未知商品 ${creative.product}`)
  return {
    id: creative.id,
    name: creative.name,
    kind: creative.kind,
    duration: creative.duration,
    source: sourceOf(creative.source),
    kol: creative.kol,
    licenseUntil: creative.source === 'kol' ? (licenseDates[creative.id] ?? '2027-03-31') : null,
    productId,
    cover: `assets/${creative.cover}`,
    clip: creative.clip ? `assets/${creative.clip}` : null,
    createdAt: seededDate(creative.id),
    seeded: true,
    compliance: creative.compliance,
  }
})

if (import.meta.env.DEV) {
  const assertUnique = (field: 'cover' | 'clip') => {
    const owners = new Map<string, string>()
    for (const creative of initialCreatives) {
      const value = creative[field]
      if (!value) continue
      const first = owners.get(value)
      if (first) throw new Error(`素材 ${field} 必须逐条唯一：${first} / ${creative.id} 共用 ${value}`)
      owners.set(value, creative.id)
    }
  }
  assertUnique('cover')
  assertUnique('clip')
  for (const creative of initialCreatives) {
    const isVideo = creative.kind === 'video'
    if (isVideo !== Boolean(creative.clip) || isVideo !== Boolean(creative.duration)) {
      throw new Error(`素材 ${creative.id} 的 kind / clip / duration 不一致`)
    }
  }
}

export function pickProductAsset(
  productId: string,
  kind: CreativeKind,
  index: number,
): Pick<LibraryCreative, 'cover' | 'clip'> {
  const fallback = initialCreatives[0]
  if (!fallback) throw new Error('素材库为空，无法为运行态素材选取资产')
  const productSeeds = initialCreatives
    .filter((creative) => creative.productId === productId)
    .sort((left, right) => left.id.localeCompare(right.id))
  const coverSeeds = productSeeds.length > 0 ? productSeeds : initialCreatives
  const videoSeeds = productSeeds.filter((creative) => creative.kind === 'video')
  const clipSeeds = videoSeeds.length > 0
    ? videoSeeds
    : initialCreatives.filter((creative) => creative.kind === 'video')
  const cover = coverSeeds[index % coverSeeds.length]?.cover ?? fallback.cover
  const clip = kind === 'video' ? (clipSeeds[index % clipSeeds.length]?.clip ?? null) : null
  return { cover, clip }
}

export function cloneInitialCreatives(): LibraryCreative[] {
  return structuredClone(initialCreatives)
}
